import { memo, useMemo, useState, useEffect } from 'react';
import { WrappedCareTeam } from 'src/careflow/ph-core/fhir-client/fhir-base/wrappers/CareTeam';
import useGridFilters, { GridFilters } from 'src/careflow/ph-core/grid/hook/useGridFilters';
import { CRSGridProps, CareTeamPermissions } from 'src/@types/crs/case';
import { WrappedPatient } from 'src/careflow/ph-core/fhir-client/fhir-base/wrappers/Patient';
import { WrappedCarePlan } from 'src/careflow/ph-core/fhir-client/fhir-base/wrappers/CarePlan';
import { Reference } from 'src/careflow/ph-core/fhir-client/resources';
import { debounce } from 'src/utils/timers';
import { searchIfContainedInObj } from 'src/careflow/helpers/common';
import { PermissionsProvider } from 'src/careflow/ph-core/admin/permissions/context/PermissionsContext';
import { CollapsibleProvider } from 'src/contexts/CollapsibleContext';
import CareTeamGrid, { CareTeamFilters, initialCareTeamFilters } from 'src/careflow/care-coordination/case/care-team-management/CareTeamGrid/CareTeamGrid';
interface CaseCareTeamGridProps extends CRSGridProps {
  patient: WrappedPatient;
  careTeams: WrappedCareTeam[];
  onMemberUpdateSuccess: Function;
  carePlan: WrappedCarePlan;
  openCollapseExternal: boolean;
  permissions: CareTeamPermissions;
  showAddButton?: boolean;
  title?: string;
  isCollapsible?: boolean;
}

const CaseCareTeamGrid = memo(
  ({
    careTeams,
    patient,
    onMemberUpdateSuccess,
    onSuccessfulCreation,
    onSuccessfulEdit,
    carePlan,
    openCollapseExternal,
    permissions,
    showAddButton = false,
    title,
    isCollapsible = true
  }: CaseCareTeamGridProps) => {
    const [filteredCareTeams, setFilteredCareTeams] = useState<WrappedCareTeam[]>([]);

    const { filters, searchTextFieldValue, ...gridFiltersProps } =
      useGridFilters<CareTeamFilters>(initialCareTeamFilters);

    const handleFiltersChange = useMemo(
      () =>
        debounce((careTeams, filters, searchTextFieldValue) => {
          setFilteredCareTeams(filterCareTeams(careTeams, { filters, searchTextFieldValue }));
        }, 800),
      []
    );

    useEffect(() => {
      handleFiltersChange(careTeams, filters, searchTextFieldValue);
    }, [filters, searchTextFieldValue, careTeams, handleFiltersChange]);

    const managingOrganizations = useMemo(
      () =>
        careTeams?.reduce<Reference[]>(
          (allManagingOrganizations, careTeam) => [
            ...allManagingOrganizations,
            ...(careTeam?.managingOrganization?.reduce<Reference[]>((managingOrganizations, mg) => {
              if (
                managingOrganizations.find(({ reference }) => reference === mg.reference) ||
                allManagingOrganizations.find(({ reference }) => reference === mg.reference)
              )
                return managingOrganizations;
              return [...managingOrganizations, mg];
            }, []) ?? []),
          ],
          []
        ),
      [careTeams]
    );

    const [openCollapse, setOpenCollapse] = useState(false);

    useEffect(() => {
      setOpenCollapse(openCollapseExternal);
    }, [openCollapseExternal]);

    return (
      <PermissionsProvider permissions={permissions}>
        <CollapsibleProvider
          title={title}
          openCollapseExternal={openCollapseExternal}
          isCollapsible={isCollapsible}
        >
          <CareTeamGrid
            searchTextFieldValue={searchTextFieldValue}
            carePlan={carePlan}
            patient={patient}
            managingOrganizations={managingOrganizations}
            onMemberUpdateSuccess={onMemberUpdateSuccess}
            onSuccessfulCreation={onSuccessfulCreation}
            onSuccessfulEdit={onSuccessfulEdit}
            careTeams={filteredCareTeams}
            filterValues={filters}
            showAddButton={showAddButton}
            {...gridFiltersProps}
          />
        </CollapsibleProvider>
      </PermissionsProvider>
    );
  }
);

const filterCareTeams = (
  careTeams: WrappedCareTeam[],
  { filters, searchTextFieldValue }: GridFilters<CareTeamFilters>
): WrappedCareTeam[] => {
  let filteredCareTeams = [...careTeams];

  const searchByString = searchTextFieldValue?.toLowerCase().trim() ?? '';
  if (searchByString.length >= 1) {
    filteredCareTeams = filteredCareTeams.filter((careTeam) =>
      searchIfContainedInObj(
        careTeam,
        [
          'name',
          'noOfMembers',
          'plainCategory',
          'managingOrganizationName',
          'severityStatus.message',
        ],
        searchByString
      )
    );
  }

  const { category, managingOrganization, status } = filters;

  return filteredCareTeams.filter((careTeam) => {
    if (status.length) {
      if (!status.some((status) => status.code === careTeam?.status)) return false;
    }

    if (managingOrganization.length) {
      if (
        !managingOrganization.some((mg) =>
          careTeam?.managingOrganization?.find((ctMG) => ctMG.reference === mg.reference)
        )
      )
        return false;
    }

    if (category.length) {
      if (
        !category.some((cat) =>
          careTeam?.category?.find((ctCat) => ctCat?.coding?.[0]?.code === cat.code)
        )
      )
        return false;
    }

    return true;
  });
};
export default CaseCareTeamGrid;
